import React, { useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import HoverDetectionContainer from '../../../../../v2BaseComponents/HoverDetectionContainer';
import { GridRefs } from '../../../../../v2BaseComponents/RichTable/types';
import { useRerenderOnInterval } from '../../../../../../hooks/useRerenderOnInterval';
import { colors } from '../../../../../../utils/theme';
import IconButton from '../../../../../v2BaseComponents/IconButton';

type RowActionsOwnProps = {
    gridInputRefs: GridRefs;
    hoveredRowIndex: number | null;
    containerRef: View | null;
    onAddRow: (afterIndex: number) => void;
    onDeleteRow: (index: number) => void;
};

export type RowActionsProps = RowActionsOwnProps;

type HoveredRowPosition = {
    top: number;
    height: number;
    rowIndex: number;
};

const ACTION_BUTTON_WIDTH = 30;

export const RowActions: React.FC<RowActionsProps> = (props) => {
    const appearanceAnimation = useRef<Animated.Value>(new Animated.Value(0));
    const focusedAnimation = useRef<Animated.Value>(new Animated.Value(0));
    const currentHoveredRowPosition = useRef<HoveredRowPosition | null>(null);
    const lockedPosition = useRef<HoveredRowPosition | null>(null);
    const [isFocused, setIsFocused] = React.useState(false);

    useEffect(() => {
        if (lockedPosition.current !== null) return;

        appearanceAnimation.current.setValue(0);
        if (props.hoveredRowIndex !== null) {
            appearanceAnimation.current.setValue(0);
            Animated.timing(appearanceAnimation.current, {
                toValue: 1,
                duration: 200,
                useNativeDriver: true,
            }).start();
        }
    }, [props.hoveredRowIndex]);

    useEffect(() => {
        Animated.timing(focusedAnimation.current, {
            toValue: isFocused ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [isFocused]);

    useRerenderOnInterval({
        isDisabled:
            props.hoveredRowIndex === null &&
            currentHoveredRowPosition.current === null &&
            lockedPosition.current === null,
        interval: 30,
    });
    const currentRef =
        props.hoveredRowIndex === null
            ? null
            : props.gridInputRefs[props.hoveredRowIndex].filter((col) => !!col)?.[0] || null;

    useEffect(() => {
        if (currentRef && props.containerRef && props.hoveredRowIndex !== null) {
            currentRef.measureLayout(props.containerRef, (x, y, width, height) => {
                currentHoveredRowPosition.current = { top: y, height, rowIndex: props.hoveredRowIndex! };
            });
        } else {
            currentHoveredRowPosition.current = null;
        }
    });

    const refPositionToUse = lockedPosition.current || currentHoveredRowPosition.current;

    const renderInActionButtonContainer = (children: React.ReactNode) => (
        <View
            style={{
                width: ACTION_BUTTON_WIDTH,
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {children}
        </View>
    );

    return (
        <View
            style={{
                width: 0,
                height: '100%',
                position: 'relative',
            }}
        >
            {refPositionToUse !== null ? (
                <HoverDetectionContainer
                    onHoverChange={(isHovered) => {
                        if (isHovered) {
                            lockedPosition.current = currentHoveredRowPosition.current;
                        } else {
                            lockedPosition.current = null;
                            setIsFocused(false);
                        }
                    }}
                >
                    <Animated.View
                        style={{
                            position: 'absolute',
                            top: refPositionToUse.top - 2,
                            opacity: appearanceAnimation.current,
                            left: focusedAnimation.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -1 * ACTION_BUTTON_WIDTH * 2 - 10],
                            }),
                            width: focusedAnimation.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [ACTION_BUTTON_WIDTH, ACTION_BUTTON_WIDTH * 3 + 10],
                            }),
                            height: refPositionToUse.height + 2,
                            backgroundColor: colors.Dividers,
                            borderColor: colors['Paper-Border'],
                            borderWidth: 1,
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            overflow: 'hidden',
                            flexDirection: 'row',
                            padding: 4,
                            borderTopEndRadius: 5,
                            borderBottomEndRadius: 5,
                            borderBottomLeftRadius: focusedAnimation.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, 5],
                            }),
                            borderTopLeftRadius: focusedAnimation.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, 5],
                            }),
                        }}
                    >
                        <Animated.View
                            style={{ height: '100%', opacity: focusedAnimation.current, alignItems: 'flex-end' }}
                        >
                            {renderInActionButtonContainer(
                                <IconButton
                                    icon="Plus"
                                    variant="minimal"
                                    onPress={() => {
                                        props.onAddRow(refPositionToUse.rowIndex);
                                    }}
                                    testID="row-action-add"
                                />,
                            )}
                        </Animated.View>
                        <Animated.View
                            style={{ height: '100%', opacity: focusedAnimation.current, alignItems: 'center' }}
                        >
                            {renderInActionButtonContainer(
                                <IconButton
                                    icon="Trash2"
                                    variant="minimal"
                                    onPress={() => {
                                        props.onDeleteRow(refPositionToUse.rowIndex);
                                    }}
                                    testID="row-action-delete"
                                />,
                            )}
                        </Animated.View>
                        {renderInActionButtonContainer(
                            <IconButton
                                icon="MoreVertical"
                                variant="minimal"
                                onPress={() => {
                                    setIsFocused(!isFocused);
                                }}
                                testID="row-action-prompt-options"
                            />,
                        )}
                    </Animated.View>
                </HoverDetectionContainer>
            ) : null}
        </View>
    );
};
